import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRouter } from "next/compat/router";
import type { PropsWithChildren } from "react";

import LoadingAnimation from "~/components/LoadingAnimation";

export interface LayoutCardProps extends PropsWithChildren<{}> {
	title: string;
	subtitle?: string;
	isLoading?: boolean;

	className?: string;

	hasEnterExitAnimations?: boolean;

	button?: {
		direction?: "left" | "right";
		handleClick?: () => void;
		label: string;
		href?: string;
	};
}

const LayoutCard = ({
	children,
	isLoading,
	title,
	subtitle,
	button,
	className,
	hasEnterExitAnimations,
}: LayoutCardProps) => {
	const router = useRouter();

	return (
		<motion.div
			className={`layout-card border ${className}`}
			{...(hasEnterExitAnimations
				? {
						initial: { opacity: 0, y: 10 },
						animate: { opacity: 1, y: 0, transition: { duration: 0.25 } },
						exit: { opacity: 0, y: 10, transition: { duration: 0.15 } },
					}
				: {})}
		>
			<div className="flex flex-col gap-2 text-center">
				<h1 className="font-semibold phablet:text-2xl text-xl">{title}</h1>
				{subtitle && <p className="font-medium">{subtitle}</p>}{" "}
			</div>

			<AnimatePresence mode="wait">
				{isLoading ? (
					<motion.div
						animate={{
							opacity: 1,
							transition: {
								duration: 0.2,
							},
						}}
						exit={{
							opacity: 0,
							transition: {
								duration: 0.2,
							},
						}}
						className="mx-auto pt-16 pb-24"
						key="loading"
					>
						<LoadingAnimation />
					</motion.div>
				) : (
					<motion.div
						animate={{
							opacity: 1,
							transition: {
								duration: 0.2,
							},
						}}
						key="content"
						exit={{
							opacity: 0,
							transition: {
								duration: 0.2,
							},
						}}
					>
						{children}
					</motion.div>
				)}
			</AnimatePresence>

			{button && (
				<button
					className={clsx(
						"mx-auto -mb-3 flex max-w-max items-center justify-center rounded-full font-medium hover:underline hover:brightness-95 lg:-mb-3",
					)}
					onClick={
						button.href ? () => router?.push(button.href!) : button.handleClick
					}
				>
					{button.direction !== "right" && <ChevronLeft size={20} />}

					{button.label}

					{button.direction === "right" && <ChevronRight size={20} />}
				</button>
			)}
		</motion.div>
	);
};

export default LayoutCard;
